import {
    Button, Checkbox, DatePicker,
    Divider,
    Form,
    InputNumber, message,
    Modal,
    Segmented, Space,
    Table, Tooltip, Typography
} from "antd";
import {
    FileTs,
    TemplateDocumentType,
} from "../../../model/models";
import React, {useEffect, useState} from "react";
import {HttpService} from "../../../services/http/Http.service";
import {HttpErrorService} from "../../../services/http/HttpError.service";
import type {TableProps} from "antd/es/table";
import {UtilsService} from "../../../services/Utils.service";
import {CloseOutlined, EditOutlined, InfoCircleOutlined, SaveFilled} from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import CustomSpinner from "../../../components/spinner/CustomSpinner";
import AddressForm from "./components/AddressForm";
import CommonFormFields from "./components/CommonFormFields";

interface Props {
    visible: boolean,
    onClose: () => void,
    simulatorId?: number | undefined,
    personId?: number | undefined,
    personCF: string,
    protocolNumber?: string,
    protocolDate?: string,
    refreshPractices?: (() => void) | undefined,
    refreshDocuments?: (() => (void | undefined)) | undefined,
    refreshInstallments?: () => Promise<void>,
    hasAnyInstallmentsPaid?: boolean,
    hasInstallments?: boolean
}

export default function Recalculation({
                                          visible,
                                          onClose,
                                          simulatorId,
                                          personId,
                                          personCF,
                                          protocolNumber,
                                          protocolDate,
                                          refreshPractices,
                                          refreshDocuments,
                                          refreshInstallments,
                                          hasAnyInstallmentsPaid,
                                          hasInstallments
                                      }: Props) {

    const [listOfPractices, setListOfPractices] = useState<FileTs[]>([])
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
    const [selectedRows, setSelectedRows] = useState<number[]>([])
    const [uploading, setUploading] = useState(false);
    const [editingRowId, setEditingRowId] = useState<number | null>(null);
    const [editedTicketValue, setEditedTicketValue] = useState<number | null>(null);
    const [recalculationForm] = Form.useForm();
    const [autoFillAddress, setAutoFillAddress] = useState<boolean>(false);
    const disableAddressFields = autoFillAddress;
    const [templateType, setTemplateType] = useState<string>(TemplateDocumentType.RICALCOLO_SEPARATO)
    const [sanction, setSanction] = useState<number>();
    const [LR, setLR] = useState<boolean>(false);

    const segmentedOptions = [
        {label: "Separato", value: TemplateDocumentType.RICALCOLO_SEPARATO},
        {label: "Ricette", value: TemplateDocumentType.RICALCOLO_RICETTE},
        ...((LR && hasInstallments && !hasAnyInstallmentsPaid) ? [{
            label: "LR No Rate Pagate",
            value: TemplateDocumentType.RICALCOLO_LR_NO_RATEPAGATE
        }] : []),
        ...((LR && hasInstallments && hasAnyInstallmentsPaid) ? [{
            label: "LR Rate Pagate",
            value: TemplateDocumentType.RICALCOLO_LR_RATEPAGATE
        }] : []),
    ];

    const detailModalColumnsEditable = [
        {
            title: "Codice esenzione",
            dataIndex: "exemptionCode",
            key: "exemptionCode",
        },
        {
            title: "Anno",
            dataIndex: "year",
            key: "year",
        },
        {
            title: "Importo ticket",
            key: "totalTicket",
            render: (record: FileTs) => {
                const isEditing = editingRowId === record.id;
                return isEditing ? (
                    <InputNumber
                        value={editedTicketValue ?? record.totalTicket}
                        min={0}
                        precision={2}
                        onChange={(value) => setEditedTicketValue(value ?? 0)}
                        formatter={(value) =>
                            value != null ? value.toString().replace(".", ",") : ""
                        }
                        parser={(value) => {
                            const normalized = value?.replace(",", ".");
                            const parsed = parseFloat(normalized ?? "");
                            return isNaN(parsed) ? 0 : parsed;
                        }}
                    />
                ) : (
                    "€ " + UtilsService.formatNumber(record.totalTicket)
                );
            },
        },
        {
            title: "Interessi",
            key: "totalInterest",
            render: (record: FileTs) => {
                if (record.firstInstallmentPaid) {
                    return (
                        <>
                            € 0
                            <Tooltip title="Interessi già pagati nella prima rata">
                                <InfoCircleOutlined
                                    style={{
                                        color: "orange",
                                        marginLeft: 6,
                                        fontSize: 15,
                                    }}
                                />
                            </Tooltip>
                        </>
                    );
                }

                return record.totalInterest > 0
                    ? `€ ${UtilsService.formatNumber(record.totalInterest)}`
                    : "No";
            },
        },
        {
            title: "Importo totale",
            key: "totalSum",
            render: (record: FileTs) => {
                const totalSum = record.totalTicket || 0;
                const interests = record.firstInstallmentPaid ? 0 : record.totalInterest;
                const total = totalSum + interests;
                return `€ ${UtilsService.formatNumber(total)}`;
            },
        },
        {
            title: "Azioni",
            key: "actions",
            render: (record: FileTs) => {
                const isEditing = editingRowId === record.id;

                if (isEditing) {
                    return (
                        <>
                            <Button
                                icon={<SaveFilled/>}
                                onClick={() => updateRow(record)}
                                style={{marginRight: 8}}
                            />
                            <Button
                                icon={<CloseOutlined/>}
                                onClick={() => {
                                    setEditingRowId(null);
                                    setEditedTicketValue(null);
                                }}
                            />
                        </>
                    );
                }

                return (
                    <Button
                        icon={<EditOutlined/>}
                        onClick={() => {
                            setEditingRowId(record.id);
                            setEditedTicketValue(record.totalTicket);
                        }}
                        disabled={record.firstInstallmentPaid}
                    />
                );
            },
        },

    ];

    const updateRow = async (record: FileTs) => {
        const updatedPractice = {
            ...record,
            totalTicket: editedTicketValue ?? 0,
        }

        try {
            const data = await HttpService.getInstanceAxios().post(
                `/file-ts/calculateInterests`,
                updatedPractice
            );
            const updatedList = listOfPractices.map(item =>
                item.id === record.id ? {
                    ...item,
                    totalInterest: data.data,
                    totalTicket: updatedPractice.totalTicket
                } : item
            );
            setListOfPractices(updatedList);
            setEditingRowId(null);
            setEditedTicketValue(null);
            message.success("Importo ticket aggiornato con successo");
        } catch (error) {
            HttpErrorService.handle("Errore durante l'aggiornamento del ticket", error, true);
        }
    }


    useEffect(() => {
        const fetchPositions = async () => {
            try {
                const response = await HttpService.getInstanceAxios().get(`/simulazione/${simulatorId}/person/${personId}`)
                setListOfPractices(response.data)
                setSanction(response.data.reduce((sum: number, item: any) => sum + (item.totalSanction || 0), 0));
            } catch (error) {
                HttpErrorService.handle("Errore durante il recupero delle pratiche", error, true)
            }
        }
        fetchPositions();
    }, []);

    const recalculate = async () => {
        try {
            const values = await recalculationForm.validateFields();
            setUploading(true);

            const selectedPractices: { [key: number]: number } = {};
            listOfPractices
                .filter((p) => !selectedRows.includes(p.id))
                .forEach((p) => {
                    selectedPractices[p.id] = p.totalTicket;
                });

            let transmittedVia
            if (values.transmissionType === "email") {
                transmittedVia = "email: " + values.email
            } else if (values.transmissionType === "pec") {
                transmittedVia = "PEC: " + values.email
            } else
                transmittedVia = values.email

            const requestBody =
                {
                    simulatorId: simulatorId,
                    personId: personId,
                    fileTsImports: selectedPractices,
                    protocolNumber: values.requestProtocolNumber,
                    protocolRequestDate: values.requestProtocolDate?.format("YYYY-MM-DD"),
                    email: transmittedVia,
                    province: values.province,
                    city: values.city,
                    cap: Number(values.cap),
                    address: values.address,
                    civic: values.houseNumber,
                    ascertainedThat: values.ascertainedThat,
                    ascertainedThatMore: values.ascertainedThatMore,
                    referencedPosition: values.referencedPosition,
                    archiveDate: protocolDate,
                    archiveNumber: protocolNumber,
                    type: templateType,
                    newSanction: tempValue,
                    deadline: values.deadline.format('YYYY-MM-DD'),
                }

            await HttpService.getInstanceAxios().post(`/file-ts/recalculate`, requestBody);
            if (refreshDocuments) {
                refreshDocuments()
            }
            if (refreshPractices) {
                refreshPractices()
            }
            if (refreshInstallments) {
                await refreshInstallments()
            }
            message.success("Ricalcolo eseguito con successo");
            onClose();
        } catch (error) {
            HttpErrorService.handle("Errore durante il ricalcolo", error, true);
        } finally {
            setUploading(false);
        }
    };

    const rowSelection: TableProps<FileTs>['rowSelection'] = {
        selectedRowKeys: selectedRowKeys,
        onChange: (selectedRowKeys: React.Key[], selectedRows: FileTs[]) => {
            setSelectedRowKeys(selectedRowKeys)
            setSelectedRows(selectedRows.map((record: FileTs) => record.id))
        }
    };

    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(0);
    const [tempValue, setTempValue] = useState(0);

    useEffect(() => {
        if (sanction) {
            setValue(sanction);
            setTempValue(sanction);
        }
    }, [sanction]);

    // pratiche importate con condono → sanzione non modificabile
    const sanctionEditingDisabled =
        listOfPractices.length > 0 && listOfPractices.every(p => p.consideraSanzione === false);

    const handleEdit = () => {
        if (sanctionEditingDisabled) return;
        setTempValue(value);
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setTempValue(value);
    };

    const handleSave = async () => {
        setValue(tempValue);
        setIsEditing(false);
    };

    return (
        <>
            <Modal
                title="Ricalcolo importo dovuto"
                open={visible}
                onCancel={() => {
                    onClose()
                }}
                onOk={recalculate}
                okText="Ricalcola"
                width={1000}
                maskClosable={false}
            >
                {listOfPractices.length > 1
                    ? "Seleziona le pratiche o modifica l'importo dei ticket"
                    : "Modifica l'importo dei ticket"}
                <Table<FileTs>
                    rowSelection={listOfPractices.length > 1 ? {
                        type: "checkbox",
                        ...rowSelection,
                        getCheckboxProps: (record) => ({
                            disabled: record.firstInstallmentPaid,
                        }),
                    } : undefined}
                    rowKey="id"
                    columns={detailModalColumnsEditable}
                    pagination={false}
                    dataSource={listOfPractices}
                />
                {(sanction !== undefined && sanction !== null && sanction !== 0) &&
                    <Space>
                        <Divider></Divider>
                        <p>Sanzione</p>
                        <InputNumber
                            style={{width: 150}}
                            value={isEditing ? tempValue : value}
                            onChange={(num) => setTempValue(num ?? 0)}
                            disabled={!isEditing}
                            min={0}
                            formatter={(num) =>
                                isEditing
                                    ? (num ?? 0).toString().replace(".", ",")  // garantisce sempre stringa
                                    : `€ ` + UtilsService.formatNumber(num ?? 0)?.toString().replace(".", ",")
                            }
                            parser={(numStr) => {
                                if (!numStr) return 0;
                                const normalized = numStr.replace("€", "").replace(/\./g, "").replace(",", ".");
                                const parsed = parseFloat(normalized);
                                return isNaN(parsed) ? 0 : parsed;
                            }}
                        />


                        {!isEditing &&
                            <Tooltip
                                title={sanctionEditingDisabled
                                    ? "Sanzione non modificabile: le pratiche sono state importate con condono"
                                    : ""}
                            >
                                <span>
                                    <Button
                                        icon={<EditOutlined/>}
                                        disabled={sanctionEditingDisabled}
                                        onClick={() => {
                                            handleEdit()
                                        }}
                                    />
                                </span>
                            </Tooltip>
                        }
                        {isEditing && (
                            <Space>
                                <Button
                                    icon={<SaveFilled/>}
                                    onClick={handleSave}
                                />
                                <Button
                                    icon={<CloseOutlined/>}
                                    onClick={handleCancel}
                                />
                            </Space>
                        )}
                    </Space>
                }
                <Form form={recalculationForm} layout="vertical">
                    <Divider>Informazioni per l'invio</Divider>
                    <Space style={{marginBottom: 40}} direction={"vertical"}>
                        <Checkbox onClick={() => setLR(!LR)}>LR 20/2025</Checkbox>
                        <Typography>Quale documento vuoi generare?</Typography>
                        <Segmented<string>
                            className="custom-segmented"
                            size={"large"}
                            color={"black"}
                            options={segmentedOptions}
                            value={templateType}
                            onChange={(value) => setTemplateType(value as TemplateDocumentType)}
                        />
                    </Space>

                    {(templateType === TemplateDocumentType.RICALCOLO_LR_NO_RATEPAGATE || templateType === TemplateDocumentType.RICALCOLO_LR_RATEPAGATE) ?
                        <CommonFormFields fieldsToShow={["email"]}/> :
                        <CommonFormFields fieldsToShow={["requestProtocolNumber", "requestProtocolDate", "email"]}/>}

                    <Form.Item
                        label="Data scadenza"
                        name="deadline"
                        rules={[
                            {required: true, message: "Inserisci la data della scadenza!"}
                        ]}
                    >
                        <DatePicker style={{width: 200}} format={"DD-MM-YYYY"}/>
                    </Form.Item>

                    {templateType === TemplateDocumentType.RICALCOLO_RICETTE &&
                        <Form.Item
                            label="Accertato che: "
                            name="ascertainedThat"
                            rules={[{required: true}]}
                        >
                            <TextArea rows={3}></TextArea>
                        </Form.Item>
                    }
                    {templateType === TemplateDocumentType.RICALCOLO_SEPARATO && (
                        <>
                            <Checkbox value="ascertainedThat">Accertato che</Checkbox>
                            <Form.Item
                                name="ascertainedThat"
                                style={{marginTop: 8}}
                            >
                                <TextArea rows={2}/>
                            </Form.Item>
                            <Checkbox value="ascertainedThatMore">Accertato, altresì, che</Checkbox>
                            <Form.Item
                                name="ascertainedThatMore"
                                style={{marginTop: 8}}
                            >
                                <TextArea rows={2}/>
                            </Form.Item>
                            <Form.Item
                                label={"Si comunica che la Sua posizione riferita"}
                                name="referencedPosition"
                                style={{marginTop: 8}}
                            >
                                <TextArea rows={1}/>
                            </Form.Item>
                        </>
                    )}
                    <AddressForm
                        autoFillAddress={autoFillAddress}
                        setAutoFillAddress={setAutoFillAddress}
                        disableAddressFields={disableAddressFields}
                        form={recalculationForm}
                        personCF={personCF}
                    />
                </Form>
            </Modal>
            <CustomSpinner
                message={"Ricalcolo in corso..."}
                open={uploading}
            />
        </>
    )
}
